import React, { useState, useEffect } from "react";
import { Checkbox, Collapse } from "antd";
import { CaretRightOutlined } from "@ant-design/icons";

const { Panel } = Collapse;

export default function TasksFilterPanel({ plansWithTasks, onFilterChange }) {
  const [checkedItems, setCheckedItems] = useState({});

  useEffect(() => {
    const initialChecked = {};
    plansWithTasks?.forEach((plan) => {
      initialChecked[`plan_${plan.id}`] = true;
      plan.tasks?.forEach((task) => {
        initialChecked[`task_${task.id}`] = true;
      });
    });
    setCheckedItems(initialChecked);
    onFilterChange(
      Object.keys(initialChecked)
        .filter((k) => k.startsWith("task_") && initialChecked[k])
        .map((k) => k.replace("task_", ""))
    );
  }, [plansWithTasks]);

  const notify = (state) => {
    const visible = Object.keys(state)
      .filter((k) => k.startsWith("task_") && state[k])
      .map((k) => k.replace("task_", ""));
    onFilterChange(visible);
  };

  const togglePlan = (planId, checked) => {
    const next = { ...checkedItems, [`plan_${planId}`]: checked };
    const plan = plansWithTasks.find((p) => p.id === planId);
    plan?.tasks?.forEach((t) => (next[`task_${t.id}`] = checked));
    setCheckedItems(next);
    notify(next);
  };

  const toggleTask = (taskId, checked) => {
    const next = { ...checkedItems, [`task_${taskId}`]: checked };
    setCheckedItems(next);
    notify(next);
  };

  const isPlanIndeterminate = (plan) => {
    const tasks = plan.tasks || [];
    if (tasks.length === 0) return false;
    const checked = tasks.filter((t) => checkedItems[`task_${t.id}`]);
    return checked.length > 0 && checked.length < tasks.length;
  };

  return (
    <div 
      className="bg-white rounded-xl shadow-lg border border-gray-100" 
      style={{ 
        height: "100%", 
        overflow: "auto",
        backgroundColor: '#f8fafc'
      }}
    >
      {/* Header */}
      <div 
        className="sticky top-0 z-10 px-6 py-4 border-b border-gray-200"
        style={{
          backgroundColor: '#3b82f6',
          borderRadius: '12px 12px 0 0'
        }}
      >
        <h3 className="text-lg font-bold text-white flex items-center">
          <span className="mr-2" style={{ 
            display: 'inline-block',
            width: '20px',
            height: '20px',
            borderRadius: '50%',
            background: 'radial-gradient(circle, #000000 0%, #000000 30%, #ffffff 30%, #ffffff 60%, #000000 60%, #000000 100%)',
            border: '2px solid #ffffff'
          }}></span>
          Filter Plans & Tasks
        </h3>
        <p className="text-blue-100 text-sm mt-1">Select plans and tasks to display in timeline</p>
      </div>

      {/* Content */}
      <div className="p-4">
        <Collapse
          ghost
          expandIcon={({ isActive }) => (
            <CaretRightOutlined 
              rotate={isActive ? 90 : 0} 
              style={{ 
                color: '#3b82f6', 
                fontSize: '14px',
                transition: 'all 0.3s ease'
              }} 
            />
          )}
          className="custom-collapse"
          style={{
            background: 'transparent'
          }}
        >
          {plansWithTasks?.map((plan, index) => (
            <Panel
              key={plan.id}
              header={
                <div 
                  className="flex items-center hover:bg-blue-50 rounded-lg px-3 py-2 transition-all duration-200" 
                  onClick={(e) => e.stopPropagation()}
                  style={{
                    margin: '-8px -12px',
                    border: '1px solid transparent'
                  }}
                >
                  <Checkbox
                    checked={!!checkedItems[`plan_${plan.id}`]}
                    indeterminate={isPlanIndeterminate(plan)}
                    onChange={(e) => togglePlan(plan.id, e.target.checked)}
                    style={{
                      transform: 'scale(1.1)'
                    }}
                  />
                  <span className="ml-3 font-semibold text-gray-800 flex-1">
                    {plan.title || plan.name}
                  </span>
                  <span 
                    className="ml-auto text-xs font-medium px-2 py-1 rounded-full"
                    style={{
                      backgroundColor: plan.tasks?.length > 0 ? '#dbeafe' : '#f5f5f5',
                      color: plan.tasks?.length > 0 ? '#1e40af' : '#757575'
                    }}
                  >
                    {plan.tasks?.length || 0} tasks
                  </span>
                </div>
              }
              style={{
                marginBottom: '8px',
                border: '1px solid #e5e7eb',
                borderRadius: '8px',
                backgroundColor: index % 2 === 0 ? '#fafbfc' : '#ffffff'
              }}
            >
              <div className="pl-8 pr-4 pb-2">
                <div className="space-y-1">
                  {plan.tasks?.map((task) => (
                    <div 
                      key={task.id} 
                      className="flex items-center py-2 px-3 rounded-md hover:bg-gray-50 transition-colors duration-150"
                      style={{
                        border: '1px solid transparent'
                      }}
                    >
                      <Checkbox
                        checked={!!checkedItems[`task_${task.id}`]}
                        onChange={(e) => toggleTask(task.id, e.target.checked)}
                      />
                      <span className="ml-3 text-sm text-gray-700 flex-1">{task.name}</span>
                      {task.assignedUserName && (
                        <span className="text-xs text-blue-700 bg-blue-100 px-2 py-1 rounded-full">
                          {task.assignedUserName}
                        </span>
                      )}
                    </div>
                  ))}
                  {(!plan.tasks || plan.tasks.length === 0) && (
                    <div 
                      className="text-gray-400 text-sm italic py-3 px-3 text-center rounded-md"
                      style={{ backgroundColor: '#f9fafb', border: '1px dashed #d1d5db' }}
                    >
                      📝 No tasks in this plan
                    </div>
                  )}
                </div>
              </div>
            </Panel>
          ))}
        </Collapse>

        {(!plansWithTasks || plansWithTasks.length === 0) && (
          <div className="text-center py-12">
            <div className="text-gray-400 text-lg mb-2">📋</div>
            <div className="text-gray-500 font-medium">No plans available</div>
            <div className="text-gray-400 text-sm">Plans will appear here when loaded</div>
          </div>
        )}
      </div>

      {/* Custom CSS */}
      <style jsx>{`
        :global(.custom-collapse .ant-collapse-item) {
          border: none !important;
          margin-bottom: 8px;
        }
        
        :global(.custom-collapse .ant-collapse-header) {
          padding: 0 !important;
          border: none !important;
        }
        
        :global(.custom-collapse .ant-collapse-content) {
          border: none !important;
          background: transparent !important;
        }
        
        :global(.custom-collapse .ant-collapse-content-box) {
          padding: 8px 0 !important;
        }
        
        :global(.ant-checkbox-wrapper:hover .ant-checkbox-inner) {
          border-color: #3b82f6 !important;
        }
        
        :global(.ant-checkbox-checked .ant-checkbox-inner) {
          background-color: #3b82f6 !important;
          border-color: #3b82f6 !important;
        }
        
        :global(.ant-checkbox-indeterminate .ant-checkbox-inner) {
          background-color: #3b82f6 !important;
          border-color: #3b82f6 !important;
        }
      `}</style>
    </div>
  );
}

